import { AUTHENTICATION_METHOD, USER_ACTIVATION_METHOD } from '../../domain/domainCodes.js'

/**
 * إعدادات متقدمة as a form — see mainForm.js. The two record lists (data sources and
 * file-storage servers) save row by row and are not part of it.
 */

export const ADVANCED_SECTIONS = {
  login: ['general', 'otp', 'lockout', 'terms'],
  registration: ['registration'],
  profile: ['profileFields', 'customAttributes', 'otherProperties'],
  sync: ['integration', 'autoIntegration'],
  services: ['services'],
}

const GROUPS = Object.values(ADVANCED_SECTIONS).flat()

const pick = (settings, key, fallback = '') => settings?.[key] ?? fallback

/** A stored `"true"`, `"True"` or boolean, as one spelling. */
export const isOn = (value) => String(value ?? '').toLowerCase() === 'true'

export function advancedInitialForm(data) {
  return Object.assign({}, ...GROUPS.map((group) => advancedDefaults(group, data)))
}

export function advancedKeys(data) {
  return Object.fromEntries(GROUPS.map((group) => [group, Object.keys(advancedDefaults(group, data))]))
}

export function advancedDefaults(section, data) {
  const s = data?.settings ?? {}
  const company = data?.company ?? {}

  switch (section) {
    case 'general':
      return {
        licenseType: pick(s, 'LicenseType'),
        companyId: company.id ?? 1,
        authenticationId: company.authenticationId ?? AUTHENTICATION_METHOD.LOCAL,
        authenticationMode: pick(s, 'AuthenticationMode', 'Forms'),
        usernameFormat: pick(s, 'UsernameFormatOptions', '1'),
      }
    case 'otp':
      return {
        otpEnabled: pick(s, 'OTPEnabled') === 'true',
        otpTarget: pick(s, 'OTPTarget', 'Admins'),
        otpLength: pick(s, 'OTPLength', '4'),
        otpValidPeriod: pick(s, 'OTPValidPeriod', '120'),
      }
    case 'lockout':
      return {
        lockoutEnabled: pick(s, 'EnableAccountLockout') === 'true',
        attempts: pick(s, 'AttemptsBeforeLockout', '5'),
        lockoutWindow: pick(s, 'AccountLockoutWindow', '5'),
        sessionTimeout: pick(s, 'SessionTimeoutInMinutes', '600'),
      }
    case 'terms':
      /* Read from what the server sent, not blanks: this is the document every user has
         to accept, and starting it empty is how one gets erased. */
      return {
        termsAr: data?.terms?.arabic ?? '',
        termsEn: data?.terms?.english ?? '',
        termsEnabled: Boolean(data?.terms?.isEnabled),
        /* Always starts off. Ticking it forces every user who has already accepted to
           accept again, so it is a decision made per save, never a remembered state. */
        termsUpdated: false,
      }
    case 'profileFields':
      return {
        visibleFields: (data?.profileFields ?? []).filter((field) => field.isVisible).map((field) => field.name),
        editableFields: (data?.profileFields ?? []).filter((field) => field.isEditable).map((field) => field.name),
      }
    case 'customAttributes':
      return Object.fromEntries(
        [1, 2, 3, 4, 5].flatMap((index) => [
          [`attribute${index}Ar`, pick(s, `Attribute${index}_AR_Name`, `Attribute${index}`)],
          [`attribute${index}En`, pick(s, `Attribute${index}_EN_Name`, `Attribute${index}`)],
        ]),
      )
    /* What is STORED, not fixed values — saving used to flip settings nobody had touched. */
    case 'otherProperties':
      return {
        allowProfileEditPage: Boolean(data?.profileOther?.allowProfileEditPage),
        forceGroupChange: Boolean(data?.profileOther?.enableForceGroupChange),
        // The group options carry numeric ids; the stored id is a string.
        forceGroupId: Number(data?.profileOther?.forceChangeGroupId) || data?.groups?.[0]?.id || '',
      }
    case 'integration':
      return {
        deptsSource: pick(s, 'DepartmentsIntegrationDataSource', 'None'),
        usersSource: pick(s, 'UsersIntegrationDataSource', 'None'),
        userFetchSource: pick(s, 'UserFetchInfoDataSource', 'None'),
        usersType: pick(s, 'UsersIntegrationType', 'Sync'),
        whenFetchedUsersCountGreaterThanLicense: pick(s, 'WhenFetchedUsersCountGreaterThanLicense', 'Reject'),
        deleteAdGroupsWithNoUsers: isOn(pick(s, 'DeleteADGroupsWithNoUsers', 'false')),
      }
    /* Only what `SaveAutoIntegrationSettings` takes. The other three values on that panel
       have no save action at all — they are shown read-only (COSMETIC_UI.md). */
    case 'autoIntegration':
      return {
        deptsAutoFetch: isOn(pick(s, 'DeptsAutoFetch', 'false')),
        deptsAutoFetchInterval: pick(s, 'DeptsAutoFetchInterval', '180'),
        usersAutoFetch: isOn(pick(s, 'UsersAutoFetch', 'false')),
        usersAutoFetchInterval: pick(s, 'UsersAutoFetchInterval', '1200'),
      }
    case 'registration':
      return {
        activateInvitations: pick(s, 'ActivateInvitations') === 'true',
        showRegistrationLink: pick(s, 'ShowRegistrationLinkOnLoginPage') === 'true',
        activateResetPassword: pick(s, 'ActivateResetPasswordPage') === 'true',
        activationBy: company.activateByAdmin
          ? String(USER_ACTIVATION_METHOD.ADMIN)
          : String(USER_ACTIVATION_METHOD.EMAIL),
        emailOnDomain: !!company.userEmailOnDomain,
        companyDomains: company.companyDomains ?? '',
      }
    /* Only what `SetServicesSettings` takes. Every service's own state is read-only — the
       action binds these three and nothing else (COSMETIC_UI.md). */
    case 'services':
      return {
        ServiceEnabledNotifications: isOn(pick(s, 'Email_ServiceState', 'false')),
        MaxRetries: pick(s, 'MaxRetries', '3'),
        MaxTaskExecutionMinutes: pick(s, 'MaxTaskExecutionMinutes', '10'),
      }
    default:
      return {}
  }
}
